<script setup lang="ts">
import { CurrencyDisplay, formatting, useLocalization, useOrderViewContext } from '@hantera/portal-app';
import { DecimalInput, TextInput, useClose } from '@hantera/design-system';
import { ref } from "vue";
import Decimal from "decimal.js";
import { v7 as uuid } from 'uuid';

const props = defineProps<{
  type: 'order' | 'shipping' | 'orderLine'
  referenceId?: string
  referenceNumber?: string
}>();

const t = useLocalization('components.order.discounts');
const tCommon = useLocalization('common');
const orderViewContext = useOrderViewContext();
const close = useClose();

const description = ref('');
const isDirty = ref(false);

const value = ref({
  value: new Decimal(0),
  isPercentage: false
});

async function save() {
  if (!isDirty.value) {
    close();
    return;
  }

  let result = false
  if (props.type === 'order') {
    result = await orderViewContext.pushPendingChange(
      undefined,
      [{
        type: 'createStaticOrderDiscount',
        discountId: uuid(),
        value: value.value.isPercentage ? value.value.value.div(100) : value.value.value,
        isPercentage: value.value.isPercentage,
        description: description.value,
      }],
      'Created discount {description} of value {textValue}',
      {
        description: description.value,
        textValue: value.value.isPercentage ?
          formatting.percent(value.value.value) :
          formatting.currency(value.value.value, orderViewContext.state.currencyCode, CurrencyDisplay.none),
        value: value.value.value,
        isPercentage: value.value.isPercentage
      }
    );
  } else if (props.type === 'shipping' && props.referenceId && props.referenceNumber) {
    result = await orderViewContext.pushPendingChange(
      undefined,
      [{
        type: 'createStaticShippingDiscount',
        discountId: uuid(),
        deliveryId: props.referenceId,
        value: value.value.isPercentage ? value.value.value.div(100) : value.value.value,
        isPercentage: value.value.isPercentage,
        description: description.value,
      }],
      'Created shipping discount {description} of value {textValue} on {deliveryNumber}',
      {
        description: description.value,
        deliveryNumber: props.referenceNumber,
        textValue: value.value.isPercentage ?
          formatting.percent(value.value.value) :
          formatting.currency(value.value.value, orderViewContext.state.currencyCode, CurrencyDisplay.none),
        value: value.value.value,
        isPercentage: value.value.isPercentage
      }
    );
  } else if (props.type === 'orderLine' && props.referenceId && props.referenceNumber) {
    result = await orderViewContext.pushPendingChange(
      undefined,
      [{
        type: 'createStaticOrderLineDiscount',
        discountId: uuid(),
        orderLineId: props.referenceId,
        value: value.value.isPercentage ? value.value.value.div(100) : value.value.value,
        isPercentage: value.value.isPercentage,
        description: description.value,
      }],
      'Created order line discount {description} of value {textValue} on {orderLine}',
      {
        description: description.value,
        orderLine: props.referenceNumber,
        textValue: value.value.isPercentage ?
          formatting.percent(value.value.value) :
          formatting.currency(value.value.value, orderViewContext.state.currencyCode, CurrencyDisplay.none),
        value: value.value.value,
        isPercentage: value.value.isPercentage
      }
    );
  }

  if (result) {
    isDirty.value = false;

    description.value = '';
    isDirty.value = false;
    value.value = {
      value: new Decimal(0),
      isPercentage: false
    };

    close();
  }
}

</script>

<template>
  <div class="static-discount">
    <div>
      <label for="discountDescription">{{ t('description') }}</label>
      <TextInput
        v-model="description"
        name="discountDescription"
        @change="isDirty = true"
      />
    </div>
    <div>
      <label for="discountValue">{{ t('value') }}</label>
      <DecimalInput
        v-model="value"
        ref="valueElem"
        :select-on-focus="true"
        :min-value="0"
        :allow-percentage="true"
        name="discountValue"
        @change="isDirty = true"
      />
    </div>
    <div class="static-discount__actions">
      <button @click="save" class="btn-primary">{{ tCommon('add') }}</button>
    </div>
  </div>
</template>

<style scoped>
.static-discount {
  margin-bottom: 5px;
  display: grid;
  grid-template-columns: auto 100px min-content;
  gap: 5px;
}

.static-discount__actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  display: flex;
  align-items: end
}
</style>